import { PropsWithChildren, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import {
  Box,
  Button,
  Divider,
  Link,
  ListItemIcon,
  MenuItem,
  MenuItemProps,
  Stack,
  Switch,
  SxProps,
  Typography,
  listClasses,
  listItemIconClasses,
  paperClasses,
} from '@mui/material';
import Menu from '@mui/material/Menu';
import { useThemeMode } from 'hooks/useThemeMode';
import { useAuth } from 'providers/AuthProvider';
import { useBreakpoints } from 'providers/BreakpointsProvider';
import { useSettingsContext } from 'providers/SettingsProvider';
import { demoUser } from 'providers/auth-provider/AuthJwtProvider';
import { languages } from 'locales/languages';
import { SET_LOCALE } from 'reducers/SettingsReducer';
import { authPaths, weddingPaths } from 'routes/paths';
import { getPostLogoutPath } from 'utils/post-logout';
import IconifyIcon from 'components/base/IconifyIcon';
import StatusAvatar from 'components/base/StatusAvatar';
import { useAssistant } from 'components/assistant/AssistantContext';

interface ProfileMenuProps {
  type?: 'default' | 'slim';
}

interface ProfileMenuItemProps extends MenuItemProps {
  icon: string;
  href?: string;
  sx?: SxProps;
}

const ProfileMenu = ({ type = 'default' }: ProfileMenuProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { up } = useBreakpoints();
  const upSm = up('sm');
  const {
    config: { textDirection, locale },
    configDispatch,
  } = useSettingsContext();

  const currentLanguage = useMemo(
    () => languages.find((lang) => lang.locale === locale) || languages[0],
    [locale],
  );
  const nextLanguage = useMemo(
    () => languages.find((lang) => lang.locale !== currentLanguage.locale) || languages[0],
    [currentLanguage],
  );

  const handleLanguageToggle = () => {
    configDispatch({ type: SET_LOCALE, payload: nextLanguage.locale });
  };

  const { isDark, setThemeMode } = useThemeMode();

  const { sessionUser, signout } = useAuth();
  const assistant = useAssistant();

  // Demo user data used for development purposes
  const user = useMemo(() => sessionUser || demoUser, [sessionUser]);
  const isCouple = user.role === 'couple';

  // On mobile the assistant is surfaced here instead of the floating launcher
  // (desktop-only). Mirror the launcher's gate: couple role + an active event,
  // never in a sandbox demo (BE 403s regardless — AI spend).
  const showAssistantItem =
    !upSm &&
    !!assistant &&
    sessionUser?.role === 'couple' &&
    !!sessionUser?.eventId &&
    !sessionUser?.isSandbox;

  const handleOpenAssistant = () => {
    handleClose();
    assistant?.openAssistant();
  };

  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSignout = () => {
    handleClose();

    // Resolve destination from the persisted role hint BEFORE clearing session,
    // so the same source of truth is used by AuthGuard / api-client.
    const target = getPostLogoutPath();

    // flushSync forces React to commit sessionUser=null and clearTokens()
    // synchronously, so guards on the next render never see a stale couple
    // session paired with a logged-out token state (the GuestGuard re-bounce).
    flushSync(() => {
      signout();
    });

    navigate(target, { replace: true });
  };

  const menuButton = (
    <Button
      color="neutral"
      variant="text"
      shape="circle"
      onClick={handleClick}
      sx={[
        {
          height: 44,
          width: 44,
        },
        type === 'slim' && {
          height: 30,
          width: 30,
          minWidth: 30,
        },
      ]}
    >
      <StatusAvatar
        alt={user.name}
        status="online"
        src={user.avatar ?? undefined}
        sx={[
          {
            width: 40,
            height: 40,
            border: 2,
            borderColor: 'background.paper',
          },
          type === 'slim' && { width: 24, height: 24, border: 1, borderColor: 'background.paper' },
        ]}
      />
    </Button>
  );
  return (
    <>
      {type === 'slim' && upSm ? (
        <Button color="neutral" variant="text" size="small" onClick={handleClick}>
          {user.name}
        </Button>
      ) : (
        menuButton
      )}
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        transformOrigin={{
          horizontal: textDirection === 'rtl' ? 'left' : 'right',
          vertical: 'top',
        }}
        anchorOrigin={{
          horizontal: textDirection === 'rtl' ? 'left' : 'right',
          vertical: 'bottom',
        }}
        sx={{
          [`& .${paperClasses.root}`]: { minWidth: 320 },
          [`& .${listClasses.root}`]: { py: 0 },
        }}
      >
        <Stack
          sx={{
            alignItems: 'center',
            gap: 2,
            px: 3,
            py: 2,
          }}
        >
          <StatusAvatar
            status="online"
            alt={user.name}
            src={user.avatar ?? undefined}
            sx={{ width: 48, height: 48 }}
          />
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                mb: 0,
              }}
            >
              {user.name}
            </Typography>
            {user.email && (
              <Typography variant="caption" color="text.secondary">
                {user.email}
              </Typography>
            )}
            {user.designation && (
              <Typography
                variant="subtitle2"
                sx={{
                  color: 'warning.main',
                }}
              >
                {user.designation}
                <IconifyIcon
                  icon="material-symbols:diamond-rounded"
                  color="warning.main"
                  sx={{ verticalAlign: 'text-bottom', ml: 0.5 }}
                />
              </Typography>
            )}
          </Box>
        </Stack>
        <Divider />
        <Box sx={{ py: 1 }}>
          {showAssistantItem && (
            <ProfileMenuItem icon="material-symbols:auto-awesome" onClick={handleOpenAssistant}>
              {t('assistant.product_name')}
            </ProfileMenuItem>
          )}

          {isCouple && (
            <>
              <ProfileMenuItem
                icon="material-symbols:settings-outline-rounded"
                onClick={handleClose}
                href={`${weddingPaths.coupleSettings}?tab=profile-security`}
              >
                {t('nav.profile.account_settings')}
              </ProfileMenuItem>

              <ProfileMenuItem
                icon="material-symbols:credit-card-outline"
                onClick={handleClose}
                href={`${weddingPaths.coupleSettings}?tab=billing-plan`}
              >
                {t('nav.profile.billing_plan')}
              </ProfileMenuItem>
            </>
          )}

          <ProfileMenuItem onClick={handleLanguageToggle} icon="material-symbols:translate-rounded">
            {t('nav.profile.language')}
            <Stack direction="row" sx={{ alignItems: 'center', gap: 0.75, ml: 'auto' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {currentLanguage.label}
              </Typography>
              <IconifyIcon icon={currentLanguage.icon} sx={{ fontSize: 20 }} />
            </Stack>
          </ProfileMenuItem>

          <ProfileMenuItem
            onClick={() => setThemeMode()}
            icon="material-symbols:dark-mode-outline-rounded"
          >
            {t('nav.profile.dark_mode')}
            <Switch checked={isDark} onChange={() => setThemeMode()} sx={{ ml: 'auto' }} />
          </ProfileMenuItem>
        </Box>
        <Divider />
        <Box sx={{ py: 1 }}>
          {sessionUser ? (
            <ProfileMenuItem onClick={handleSignout} icon="material-symbols:logout-rounded">
              {t('common.sign_out')}
            </ProfileMenuItem>
          ) : (
            <ProfileMenuItem href={authPaths.login} icon="material-symbols:login-rounded">
              {t('common.sign_in')}
            </ProfileMenuItem>
          )}
        </Box>
      </Menu>
    </>
  );
};

const ProfileMenuItem = ({
  icon,
  onClick,
  children,
  href,
  sx,
}: PropsWithChildren<ProfileMenuItemProps>) => {
  const linkProps = href ? { component: Link, href, underline: 'none' } : {};
  return (
    <MenuItem onClick={onClick} {...linkProps} sx={{ gap: 1, ...sx }}>
      <ListItemIcon
        sx={{
          [`&.${listItemIconClasses.root}`]: { minWidth: 'unset !important' },
        }}
      >
        <IconifyIcon icon={icon} sx={{ color: 'text.secondary' }} />
      </ListItemIcon>
      {children}
    </MenuItem>
  );
};

export default ProfileMenu;
